import { ClientService, ToastMessageService } from '@fitness-force/api';
import { Store } from '@ngrx/store';
import { SlicePipe, TitleCasePipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';

import { BehaviorSubject } from 'rxjs';
import { STAFF_STATE } from '@fitness-force/state';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { MessageModule } from 'primeng/message';
import { ChipModule } from 'primeng/chip';
import type { ChangeBulkClientRepresentativeRequest } from '@fitness-force/models';
import { ClientFilterPipe } from '@fitness-force/pipes/common-pipes';

@Component({
  selector: 'change-client-rep-bulk',
  templateUrl: './change-client-rep-bulk.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    PasswordModule,
    ShortNamePipe,
    AvatarModule,
    AssignedToComponent,
    MessageModule,
    ChipModule,
    ClientFilterPipe,
    TranslateModule,
    SlicePipe,
    TitleCasePipe,
  ],
})
export class ChangeClientRepBulkComponent implements OnInit {
  private store = inject(Store);
  translate = inject(TranslateService);
  private clientService = inject(ClientService);
  private fb = inject(UntypedFormBuilder);
  private toastMessageService = inject(ToastMessageService);
  cdr = inject(ChangeDetectorRef);

  readonly visible = input<boolean>();
  readonly clientData = input(new BehaviorSubject<any>(null));
  readonly tenantId = input<number>(0);
  readonly closeChangeRepresentativeDialog = output<
    { action: string } | boolean
  >();

  showPopup: boolean = true;
  showAllMembers: boolean = false;

  currentStaff$ = new BehaviorSubject<any>({});
  selectedStaff$ = new BehaviorSubject<any>({});
  allStaffs$ = new BehaviorSubject<any>([]);
  updateClientRepFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  isClientRepresentative = new BehaviorSubject(true);
  claims: string[] = ['can_be_client_rep'];
  isloading: boolean = false;

  clients: any = [];
  clientIds: any = [];
  clientsearch: string = '';

  ngOnInit(): void {
    this.componentInit();
  }
  componentInit() {
    this.updateClientRepFormGroup = this.fb.group({
      newRepresentative: ['', [Validators.required]],
    });
    const clientDataSubject = this.clientData();
    if (clientDataSubject) {
      clientDataSubject.subscribe((clients: any) => {
        if (clients && clients.length > 0) {
          clients.forEach((c: { id: any; full_name: any; image: any }) => {
            this.clients.push({ id: c.id, name: c.full_name, image: c.image });
            this.clientIds.push(c.id);
          });
        }
        this.store.select(STAFF_STATE).subscribe((staff: any) => {
          this.allStaffs$.next(staff.staffList);
        });
      });
    }
  }

  get NewRepresentative(): UntypedFormControl {
    return this.updateClientRepFormGroup.get(
      'newRepresentative',
    ) as UntypedFormControl;
  }

  selectStaff(staff: any) {
    this.selectedStaff$.next(staff);
    this.NewRepresentative.setValue(staff.id);
  }

  updateClientRepresentative() {
    this.isloading = true;
    let changeBulkClientRepresentativeRequest: ChangeBulkClientRepresentativeRequest =
      {} as ChangeBulkClientRepresentativeRequest;
    changeBulkClientRepresentativeRequest.clients = this.clientIds;
    changeBulkClientRepresentativeRequest.client_representative_id =
      this.NewRepresentative.value.id;
    this.clientService
      .changeClientRepresentativeBulk(
        this.tenantId(),
        changeBulkClientRepresentativeRequest,
      )
      .subscribe({
        next: async (res: any) => {
          this.translate
            .get('SALE_REPRESENTATIVE.UPDATE_MESSAGE')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.isloading = false;
          await this.timeout(1000);
          this.closeDialogEvent(true);
        },
        error: (error) => {
          this.isloading = false;
          this.toastMessageService.showError(error);
          this.cdr.detectChanges();
        },
      });
  }

  closeDialogEvent(updated = false) {
    this.closeChangeRepresentativeDialog.emit(
      updated ? { action: 'updated' } : updated,
    );
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
